import React from 'react';
import { Users, UserPlus, Target, Megaphone, Briefcase } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ModernModuleDashboard from '../../shared/components/dashboard/ModernModuleDashboard';
import {
  buildDashboardCharts,
  pipelineFromKpis,
  statusFromChartRows,
  sumRowValues,
  withKpiVariants,
} from '../../shared/components/dashboard/moduleDashboardCharts';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';
import { ALL_CRM_DATA_REFRESH_EVENTS } from './crmRefreshEvents';

export default function CrmDashboardPage() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.crm.pages.dashboardPage.${key}`, opts);

  return (
    <ModernModuleDashboard
      apiEndpoint="/crm/dashboard"
      refreshEvents={ALL_CRM_DATA_REFRESH_EVENTS}
      title={p('title', { defaultValue: 'لوحة تحكم علاقات العملاء' })}
      subtitle={p('subtitle', { defaultValue: 'مؤشرات العملاء والفرص والحملات' })}
      icon={Users}
      accent="from-cyan-600 to-teal-800"
      recentTitle={p('recent_interactions', { defaultValue: 'آخر التفاعلات' })}
      quickLinks={[
        { label: p('link_clients', { defaultValue: 'العملاء' }), href: '/relation-client/clients', icon: Users },
        { label: p('link_leads', { defaultValue: 'العملاء المحتملون' }), href: '/crm/leads', icon: UserPlus },
        { label: p('link_pipeline', { defaultValue: 'مسار المبيعات' }), href: '/crm/pipeline', icon: Target },
        { label: p('link_campaigns', { defaultValue: 'الحملات' }), href: '/crm/campaigns', icon: Megaphone },
      ]}
      mapData={(data) => {
        const kpis = data.kpis || {};
        const chartRows = data.chartRows || [];
        const { active, inactive } = statusFromChartRows(chartRows, [
          'open',
          'qualified',
          'proposal',
          'negotiation',
          'won',
          'active',
        ]);

        return {
          hero: {
            label: p('kpi_pipeline_value', { defaultValue: 'قيمة المسار' }),
            value: formatBrandMoney(kpis.pipelineValue || 0),
            icon: Target,
          },
          kpis: withKpiVariants([
            { label: p('kpi_clients', { defaultValue: 'العملاء' }), value: kpis.totalClients ?? 0, icon: Users },
            { label: p('kpi_leads', { defaultValue: 'عملاء محتملون' }), value: kpis.activeLeads ?? 0, icon: UserPlus },
            { label: p('kpi_campaigns', { defaultValue: 'حملات نشطة' }), value: kpis.activeCampaigns ?? 0, icon: Megaphone },
            {
              label: p('kpi_opportunities', { defaultValue: 'الفرص' }),
              value: sumRowValues(chartRows),
              icon: Briefcase,
            },
          ]),
          charts: buildDashboardCharts({
            pipeline: {
              title: p('chart_pipeline', { defaultValue: 'مراحل الفرص' }),
              subtitle: p('chart_pipeline_subtitle', { defaultValue: 'عملاء وفرص وحملات' }),
              alwaysShow: true,
              rows: pipelineFromKpis([
                { label: p('kpi_clients', { defaultValue: 'العملاء' }), value: kpis.totalClients ?? 0 },
                { label: p('kpi_leads', { defaultValue: 'عملاء محتملون' }), value: kpis.activeLeads ?? 0 },
                { label: p('kpi_campaigns', { defaultValue: 'حملات نشطة' }), value: kpis.activeCampaigns ?? 0 },
              ]),
            },
            status: {
              title: p('chart_status_title', { defaultValue: 'حالة الفرص' }),
              subtitle: p('chart_status_subtitle', { defaultValue: 'نشطة مقابل مغلقة' }),
              active: active || kpis.activeLeads || 0,
              inactive: inactive || 0,
              alwaysShow: true,
            },
            ranked: {
              title: p('chart_ranked_title', { defaultValue: 'أبرز مراحل المسار' }),
              subtitle: p('chart_ranked_subtitle', { defaultValue: 'حسب مرحلة الفرصة' }),
              rows: chartRows,
            },
          }),
          recentItems: data.recentItems || [],
        };
      }}
    />
  );
}
